import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { FaHeart, FaTimes } from 'react-icons/fa';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Badge modal que muestra el mensaje del día de la coach.
 * - Aparece automáticamente al abrir el dashboard (una vez por día por mensaje).
 * - La clienta puede leerlo, guardarlo en favoritos (❤️) o descartarlo.
 * - Después de leído/descartado no reaparece en la misma sesión.
 */
export default function CoachMessageBadge() {
  const [payload, setPayload] = useState(null);
  const [open, setOpen] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('deisycfit_token');
    if (!token) return;

    const fetchToday = async () => {
      try {
        const { data } = await axios.get(`${API}/coach-messages/today`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const msg = data?.message;
        if (!msg) return;

        // No mostrar si la clienta ya lo leyó/descartó (persistente en DB).
        // Se sigue pudiendo ver en la lista de favoritos si lo guardó.
        if (data.read || data.dismissed) return;

        // Evitar re-abrir en la misma sesión si ya lo cerró
        const sessionKey = `coach_msg_seen_${msg.id}`;
        if (sessionStorage.getItem(sessionKey)) return;

        setPayload(data);
        // Delay pequeño para que no compita con otros modales al cargar
        setTimeout(() => setOpen(true), 800);
      } catch (e) {
        // Silencioso: no bloquea el dashboard si falla
        console.warn('[coach-badge] no se pudo cargar mensaje:', e?.response?.status || e?.message);
      }
    };
    fetchToday();
  }, []);

  const message = payload?.message;

  const markRead = async () => {
    const token = localStorage.getItem('deisycfit_token');
    try {
      await axios.post(`${API}/coach-messages/${message.id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (e) { /* noop */ }
  };

  /**
   * Botón "GUARDAR":
   *   1. Marca el mensaje como favorited=true (aparece en la bandeja del sobre).
   *   2. Lo oculta del Dashboard sin descartarlo (read_at) — SIGUE visible en Inbox.
   */
  const saveAndClose = async () => {
    if (!message || dismissing) return;
    setDismissing(true);
    const token = localStorage.getItem('deisycfit_token');
    try {
      // 1) Marcar como favorito
      await axios.post(`${API}/coach-messages/${message.id}/favorite`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // 2) Marcar como leído para que no reaparezca en el dashboard
      await markRead();
    } catch (e) { /* noop */ }
    sessionStorage.setItem(`coach_msg_seen_${message.id}`, '1');
    setOpen(false);
    setTimeout(() => setPayload(null), 300);
  };

  /**
   * Botón "¡ENTENDIDO!":
   *   Marca el mensaje como dismissed=true → NUNCA vuelve a aparecer,
   *   ni en el Dashboard ni en la bandeja de favoritos.
   */
  const dismissForever = async () => {
    if (!message || dismissing) return;
    setDismissing(true);
    const token = localStorage.getItem('deisycfit_token');
    try {
      await axios.post(`${API}/coach-messages/${message.id}/dismiss`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (e) { /* noop */ }
    sessionStorage.setItem(`coach_msg_seen_${message.id}`, '1');
    setOpen(false);
    setTimeout(() => setPayload(null), 300);
  };

  /**
   * Cerrar temporalmente (backdrop o X):
   *   No persiste nada en la base — solo marca como "visto en esta sesión"
   *   para que no reabra mientras la usuaria sigue navegando.
   */
  const closeTemporarily = () => {
    if (!message) return;
    sessionStorage.setItem(`coach_msg_seen_${message.id}`, '1');
    setOpen(false);
    setTimeout(() => setPayload(null), 300);
  };

  if (!message) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[160] flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }}
          onClick={closeTemporarily}
          data-testid="coach-message-badge"
        >
          <motion.div
            initial={{ scale: 0.9, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.9, y: 20, opacity: 0 }}
            transition={{ type: 'spring', damping: 22 }}
            className="relative w-full max-w-md max-h-[90vh] rounded-3xl flex flex-col overflow-hidden"
            style={{
              background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)',
              border: '1px solid rgba(var(--brand-primary-glow), 0.3)',
              boxShadow: '0 0 60px rgba(var(--brand-primary-glow), 0.25)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* ═════ HEADER STICKY: chip + botón cerrar SIEMPRE visibles ═════ */}
            <div
              className="flex-shrink-0 relative"
              style={{ background: 'linear-gradient(180deg, #1a1a2e 0%, #1a1a2e 100%)' }}
            >
              <div
                className="h-1.5 w-full"
                style={{ background: 'linear-gradient(90deg, var(--brand-primary), #00F0FF)' }}
              />
              <div className="flex items-start justify-between px-5 pt-4 pb-3">
                <span
                  className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full"
                  style={{ background: 'rgba(var(--brand-primary-glow), 0.15)', color: 'var(--brand-primary)' }}
                >
                  Mensaje de tu coach
                </span>
                <button
                  onClick={closeTemporarily}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all"
                  data-testid="coach-message-close"
                  aria-label="Cerrar"
                >
                  <FaTimes />
                </button>
              </div>
            </div>

            {/* ═════ CUERPO SCROLLEABLE (mensajes largos) ═════ */}
            <div className="flex-1 overflow-y-auto px-6 py-4 text-center">
              {/* Ícono grande + Título */}
              <div className="mb-5">
                <div
                  className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-4 mx-auto"
                  style={{
                    background: 'radial-gradient(circle at 30% 30%, rgba(var(--brand-primary-glow), 0.35), rgba(var(--brand-primary-glow), 0.08) 70%)',
                    border: '1px solid rgba(var(--brand-primary-glow), 0.35)',
                    boxShadow: '0 0 32px rgba(var(--brand-primary-glow), 0.25)',
                  }}
                >
                  <span className="text-4xl" style={{ filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.5))' }}>{message.icon || '💪'}</span>
                </div>
                <h3
                  className="text-xl md:text-2xl font-black text-white leading-tight tracking-tight"
                  style={{ fontFamily: 'Poppins, sans-serif', textShadow: '0 2px 12px rgba(0,0,0,0.35)' }}
                >
                  {message.title}
                </h3>
              </div>

              {/* Cuerpo — markdown centrado, tipografía legible */}
              <div
                className="text-slate-100/95 text-[15px] leading-relaxed mb-5 mx-auto text-left"
                style={{ maxWidth: '40ch' }}
                data-testid="coach-message-body"
              >
                <ReactMarkdown
                  components={{
                    p: ({ children }) => <p className="my-3 text-slate-100/95">{children}</p>,
                    strong: ({ children }) => <strong className="text-pink-300 font-bold">{children}</strong>,
                    em: ({ children }) => <em className="text-cyan-300/90 italic">{children}</em>,
                    h1: ({ children }) => <h4 className="text-lg font-black text-white mt-4 mb-2">{children}</h4>,
                    h2: ({ children }) => <h4 className="text-base font-black text-white mt-4 mb-2">{children}</h4>,
                    h3: ({ children }) => <h4 className="text-base font-bold text-pink-200 mt-3 mb-1.5">{children}</h4>,
                    ul: ({ children }) => <ul className="list-disc list-inside space-y-1 my-2 text-slate-100/95">{children}</ul>,
                    ol: ({ children }) => <ol className="list-decimal list-inside space-y-1 my-2 text-slate-100/95">{children}</ol>,
                    li: ({ children }) => <li className="text-slate-100/95">{children}</li>,
                    a: ({ href, children }) => (
                      <a href={href} target="_blank" rel="noopener noreferrer" className="text-cyan-300 underline">
                        {children}
                      </a>
                    ),
                    hr: () => <hr className="my-3 border-white/10" />,
                  }}
                >
                  {message.body || ''}
                </ReactMarkdown>
              </div>

              {/* Firma centrada */}
              <div
                className="inline-flex items-center gap-2 text-xs italic px-4 py-1.5 rounded-full mb-2"
                style={{
                  background: 'rgba(236, 72, 153, 0.10)',
                  border: '1px solid rgba(236, 72, 153, 0.30)',
                  color: '#F9A8D4',
                }}
              >
                <span>— Deisy Carolina</span>
                {typeof payload.week === 'number' && payload.week > 0 && (
                  <>
                    <span className="text-white/30">·</span>
                    <span className="font-semibold">Semana {payload.week}</span>
                    {typeof payload.day === 'number' && payload.day > 0 && (
                      <>
                        <span className="text-white/30">·</span>
                        <span className="font-semibold">Día {payload.day}</span>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* ═════ FOOTER STICKY: botones de acción SIEMPRE visibles ═════ */}
            <div
              className="flex-shrink-0 px-5 py-3 border-t border-white/10"
              style={{ background: 'rgba(15, 15, 25, 0.6)', backdropFilter: 'blur(4px)' }}
            >
              <div className="flex gap-2">
                <button
                  onClick={saveAndClose}
                  disabled={dismissing}
                  className="flex-1 py-3 rounded-full text-sm font-semibold flex items-center justify-center gap-2 transition-all text-pink-300 border border-pink-500/40 hover:bg-pink-500/10 disabled:opacity-60"
                  data-testid="coach-message-favorite"
                  title="Guardar en Mis Mensajes"
                >
                  <FaHeart />
                  Guardar
                </button>
                <button
                  onClick={dismissForever}
                  disabled={dismissing}
                  className="flex-1 py-3 rounded-full text-sm font-semibold text-white transition-all disabled:opacity-60"
                  style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                  data-testid="coach-message-ok"
                  title="No lo mostrar más"
                >
                  ¡Entendido!
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
